import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import productService from '../services/productService';
import productLabelService from '../services/productLabelService';

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const result = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (ch === ',' && !inQ) {
        result.push(cur.trim());
        cur = '';
      } else {
        cur += ch;
      }
    }
    result.push(cur.trim());
    return result;
  };

  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(line => {
    const values = parseRow(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = values[i] || ''; });
    return obj;
  });

  return { headers, rows };
}

function detectGtinCol(headers, rows) {
  const sample = rows.slice(0, 20);
  for (const h of headers) {
    if (/gtin/i.test(h)) return h;
  }
  let best = null, bestScore = 0;
  for (const h of headers) {
    const score = sample.filter(r => /^\d{12,14}$/.test((r[h] || '').replace(/[-\s]/g, ''))).length;
    if (score > bestScore) { bestScore = score; best = h; }
  }
  return best;
}

function detectDescCol(headers) {
  const priority = [/item.?description/i, /functional.?name/i, /description/i, /brand.?name/i, /product.?name/i, /name/i];
  for (const pat of priority) {
    const found = headers.find(h => pat.test(h));
    if (found) return found;
  }
  return headers[1] || headers[0] || '';
}

function normalize(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

function normGtin(g) {
  return (g || '').replace(/\D/g, '').padStart(14, '0');
}

function findSavedMatch(gtin, products, labelVariants) {
  for (const p of products) {
    if (p.gtin && normGtin(p.gtin) === gtin) return { type: 'product', id: p.id, confidence: 'saved' };
    for (const pres of (p.Presentations || [])) {
      if (pres.gtin && normGtin(pres.gtin) === gtin) return { type: 'presentation', id: pres.id, confidence: 'saved' };
    }
  }
  for (const v of labelVariants) {
    if (v.upc && normGtin(v.upc) === gtin) return { type: v.type, id: v.id, confidence: 'saved' };
  }
  return null;
}

function findBestMatch(desc, products, labelVariants) {
  if (!desc) return null;
  const nd = normalize(desc);

  // 1. Exact full match → product
  for (const p of products) {
    if (normalize(p.name) === nd) return { type: 'product', id: p.id, name: p.name, confidence: 'high' };
  }

  // 2. Exact full match → presentation name alone
  for (const p of products) {
    for (const pres of (p.Presentations || [])) {
      if (normalize(pres.name) === nd) return { type: 'presentation', id: pres.id, name: `${p.name} → ${pres.name}`, confidence: 'high' };
    }
  }

  // 3. Exact full match → label template or variant name alone
  for (const v of (labelVariants || [])) {
    if (normalize(v.name) === nd) return { type: v.type, id: v.id, name: v.name, confidence: 'high' };
  }

  // 4. Two-stage: product name in description → check presentation then variant in remainder.
  //    Sort longest name first so "Margherita 12"" beats "Margherita".
  const byLength = [...products].sort((a, b) => b.name.length - a.name.length);
  for (const p of byLength) {
    const nn = normalize(p.name);
    if (!nn || !nd.includes(nn)) continue;

    const remainder = nd.replace(nn, '').replace(/^[\s\-,]+/, '').replace(/[\s\-,]+$/, '').trim();

    if (remainder) {
      // Try presentations first
      for (const pres of (p.Presentations || [])) {
        const np = normalize(pres.name);
        if (!np) continue;
        if (np === remainder || remainder.includes(np) || np.includes(remainder)) {
          return { type: 'presentation', id: pres.id, name: `${p.name} → ${pres.name}`, confidence: 'high' };
        }
      }
      // Then try label templates/variants belonging to this product
      const productVariants = (labelVariants || []).filter(v => v.productId === p.id);
      for (const v of productVariants) {
        const nv = normalize(v.name);
        if (!nv) continue;
        if (nv === remainder || remainder.includes(nv) || nv.includes(remainder)) {
          return { type: v.type, id: v.id, name: v.name, confidence: 'high' };
        }
      }
    }

    // Product matched, nothing more specific found
    return { type: 'product', id: p.id, name: p.name, confidence: remainder ? 'medium' : 'high' };
  }

  // 5. Token overlap — catches word-order differences (e.g. "10\" Veggie Pizza" ↔ "Veggie 10\" - Shredded")
  //    Count how many of the product's tokens appear anywhere in the description tokens.
  const descTokens = new Set(nd.split(' ').filter(t => t.length > 0));
  let bestScore = 0, bestProduct = null;
  for (const p of byLength) {
    const pTokens = normalize(p.name).split(' ').filter(t => t.length > 0);
    if (pTokens.length < 2) continue;
    const matched = pTokens.filter(t => descTokens.has(t)).length;
    const coverage = matched / pTokens.length;
    if (coverage >= 0.6 && matched >= 2 && coverage > bestScore) {
      bestScore = coverage;
      bestProduct = p;
    }
  }
  if (bestProduct) {
    return { type: 'product', id: bestProduct.id, name: bestProduct.name, confidence: 'medium' };
  }

  return null;
}

function MatchCombobox({ value, onChange, options }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedOpt = value ? options.find(o => o.type === value.type && o.id === value.id) : null;
  const q = query.toLowerCase();
  const filtered = q
    ? options.filter(o => o.label.toLowerCase().includes(q))
    : options;

  const productFiltered = filtered.filter(o => o.group === 'Products');
  const presFiltered = filtered.filter(o => o.group === 'Presentations');
  const labelFiltered = filtered.filter(o => o.group === 'Labels');

  const handleSelect = (opt) => {
    onChange(opt.type, opt.id);
    setQuery('');
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null, null);
    setQuery('');
    setOpen(false);
  };

  const inputValue = open ? query : (selectedOpt ? selectedOpt.label : '');

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div className="input-group input-group-sm">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Type to search…"
          value={inputValue}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setQuery(''); setOpen(true); }}
          style={{ background: selectedOpt ? '#f0fff4' : undefined }}
        />
        {selectedOpt && (
          <button className="btn btn-outline-secondary btn-sm" type="button" tabIndex={-1} onMouseDown={handleClear}>
            ✕
          </button>
        )}
      </div>
      {open && (productFiltered.length > 0 || presFiltered.length > 0 || labelFiltered.length > 0) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050,
          background: '#fff', border: '1px solid #dee2e6', borderRadius: 4,
          boxShadow: '0 4px 14px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto',
        }}>
          {productFiltered.length > 0 && (
            <>
              <div style={{ padding: '4px 10px', fontSize: 11, fontWeight: 700, color: '#6c757d', background: '#f8f9fa', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Products
              </div>
              {productFiltered.map(o => (
                <div
                  key={`product:${o.id}`}
                  onMouseDown={() => handleSelect(o)}
                  style={{
                    padding: '6px 12px', cursor: 'pointer', fontSize: 13,
                    background: selectedOpt?.id === o.id && selectedOpt?.type === 'product' ? '#e8f4ff' : undefined,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f0f7ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = selectedOpt?.id === o.id && selectedOpt?.type === 'product' ? '#e8f4ff' : ''; }}
                >
                  {o.label}
                </div>
              ))}
            </>
          )}
          {presFiltered.length > 0 && (
            <>
              <div style={{ padding: '4px 10px', fontSize: 11, fontWeight: 700, color: '#6c757d', background: '#f8f9fa', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Presentations
              </div>
              {presFiltered.map(o => (
                <div
                  key={`presentation:${o.id}`}
                  onMouseDown={() => handleSelect(o)}
                  style={{
                    padding: '6px 12px 6px 20px', cursor: 'pointer', fontSize: 13,
                    background: selectedOpt?.id === o.id && selectedOpt?.type === 'presentation' ? '#e8f4ff' : undefined,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f0f7ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = selectedOpt?.id === o.id && selectedOpt?.type === 'presentation' ? '#e8f4ff' : ''; }}
                >
                  {o.label}
                </div>
              ))}
            </>
          )}
          {labelFiltered.length > 0 && (
            <>
              <div style={{ padding: '4px 10px', fontSize: 11, fontWeight: 700, color: '#6c757d', background: '#f8f9fa', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Master Case Variant (Labels)
              </div>
              {labelFiltered.map(o => {
                const isTemplate = o.type === 'label_template';
                const isSelected = selectedOpt?.id === o.id && selectedOpt?.type === o.type;
                return (
                  <div
                    key={`${o.type}:${o.id}`}
                    onMouseDown={() => handleSelect(o)}
                    style={{
                      padding: isTemplate ? '6px 12px' : '5px 12px 5px 24px',
                      cursor: 'pointer',
                      fontSize: isTemplate ? 13 : 12,
                      fontWeight: isTemplate ? 600 : 400,
                      background: isSelected ? '#fff0e8' : undefined,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fff7f0'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isSelected ? '#fff0e8' : ''; }}
                  >
                    {o.label}
                  </div>
                );
              })}
            </>
          )}
          {filtered.length === 0 && (
            <div style={{ padding: '8px 12px', fontSize: 13, color: '#6c757d' }}>No matches for &ldquo;{query}&rdquo;</div>
          )}
        </div>
      )}
    </div>
  );
}

function GS1ImportPage() {
  const [products, setProducts] = useState([]);
  const [labelVariants, setLabelVariants] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const [parsed, setParsed] = useState(null);
  const [gtinCol, setGtinCol] = useState('');
  const [descCol, setDescCol] = useState('');
  const [showCols, setShowCols] = useState(false);

  const [matches, setMatches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      productService.getProducts({ includeInactive: false }).catch(() => []),
      productLabelService.getVariantsForImport().catch(() => []),
    ]).then(([prods, variants]) => {
      setProducts(prods);
      setLabelVariants(variants);
    }).finally(() => setLoadingProducts(false));
  }, []);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isXlsx = /\.(xlsx|xls|xlsm)$/i.test(file.name);
    const reader = new FileReader();

    reader.onload = (ev) => {
      let headers, rows;
      if (isXlsx) {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (!data || data.length < 2) { setError('Could not read the Excel file or it is empty.'); return; }
        headers = data[0].map(h => String(h).trim());
        rows = data.slice(1).map(r => {
          const obj = {};
          headers.forEach((h, i) => { obj[h] = r[i] != null ? String(r[i]).trim() : ''; });
          return obj;
        });
      } else {
        const parsed = parseCSV(ev.target.result);
        headers = parsed.headers;
        rows = parsed.rows;
      }

      if (headers.length === 0) { setError('Could not parse the file.'); return; }
      setParsed({ headers, rows });
      setGtinCol(detectGtinCol(headers, rows) || '');
      setDescCol(detectDescCol(headers) || '');
      setMatches([]);
      setSaveResult(null);
      setError('');
    };

    if (isXlsx) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  const runMatch = () => {
    if (!parsed || !gtinCol) return;
    const validRows = parsed.rows.filter(r => /\d{12,14}/.test((r[gtinCol] || '').replace(/[-\s]/g, '')));
    const result = validRows.map(r => {
      const raw = r[gtinCol].replace(/[-\s]/g, '');
      const gtin = raw.length === 14 ? raw : raw.padStart(14, '0');
      const desc = descCol ? r[descCol] : '';
      const match = findSavedMatch(gtin, products, labelVariants) || findBestMatch(desc, products, labelVariants);
      return { gtin, description: desc, match, selected: match ? { type: match.type, id: match.id } : null };
    });
    setMatches(result);
  };

  const setSelected = (idx, type, id) => {
    setMatches(prev => prev.map((m, i) => i === idx ? { ...m, selected: type ? { type, id: Number(id) } : null } : m));
  };

  const handleSave = async () => {
    const assignments = matches
      .filter(m => m.selected)
      .map(m => ({ type: m.selected.type, id: m.selected.id, gtin: m.gtin }));
    if (assignments.length === 0) { setError('No assignments to save.'); return; }
    setSaving(true);
    setError('');
    try {
      const result = await productService.confirmGtins(assignments);
      setSaveResult(result);
    } catch {
      setError('Error saving GTINs. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const allOptions = [];
  for (const p of products) {
    allOptions.push({ type: 'product', id: p.id, label: `${p.name}${p.sku ? ` (${p.sku})` : ''}${p.gtin ? ' ✓' : ''}`, group: 'Products' });
    for (const pres of (p.Presentations || [])) {
      allOptions.push({ type: 'presentation', id: pres.id, label: `${p.name} → ${pres.name}${pres.gtin ? ' ✓' : ''}`, group: 'Presentations' });
    }
  }
  for (const v of labelVariants) {
    const label = v.type === 'label_template'
      ? `${v.templateName}${v.upc ? ' ✓' : ''}`
      : `${v.productName} — ${v.name}${v.upc ? ' ✓' : ''}`;
    allOptions.push({ type: v.type, id: v.id, label, group: 'Labels' });
  }

  const confBadge = (c) => {
    if (c === 'saved') return <span className="badge text-bg-primary">saved</span>;
    if (c === 'high') return <span className="badge text-bg-success">high</span>;
    if (c === 'medium') return <span className="badge text-bg-warning text-dark">medium</span>;
    return null;
  };

  const assignedCount = matches.filter(m => m.selected).length;

  return (
    <div className="container mt-4 pb-5" style={{ maxWidth: 1000 }}>
      <div className="d-flex align-items-center gap-3 mb-3">
        <Link to="/admin/products" className="btn btn-outline-secondary btn-sm">&larr; Products</Link>
        <h4 className="mb-0">Import GTINs from GS1</h4>
      </div>

      <div className="alert alert-info mb-4" style={{ fontSize: 13 }}>
        Export your product GTINs from <strong>GS1 US Data Hub</strong> as an Excel (.xlsx) or CSV file, then upload it here. The system will automatically match each GTIN to a product or presentation so you don&apos;t have to look them up manually.
      </div>

      {loadingProducts ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : (
        <>
          {/* Step 1 */}
          <div className="card mb-3">
            <div className="card-header fw-semibold" style={{ fontSize: 14 }}>Step 1 — Upload GS1 File (.xlsx or .csv)</div>
            <div className="card-body">
              <input type="file" className="form-control" accept=".xlsx,.xls,.xlsm,.csv,.txt" onChange={handleFile} />
              {parsed && (
                <div className="mt-2 d-flex align-items-center gap-3 flex-wrap">
                  <span className="text-success" style={{ fontSize: 13 }}>
                    ✓ Loaded {parsed.rows.length} rows &mdash; {parsed.headers.length} columns detected
                  </span>
                  <button className="btn btn-link btn-sm p-0" style={{ fontSize: 12 }} onClick={() => setShowCols(v => !v)}>
                    {showCols ? 'Hide columns' : 'Show column names'}
                  </button>
                </div>
              )}
              {parsed && showCols && (
                <div className="mt-2 p-2 bg-light rounded" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                  {parsed.headers.join(' | ')}
                </div>
              )}
            </div>
          </div>

          {/* Step 2 */}
          {parsed && (
            <div className="card mb-3">
              <div className="card-header fw-semibold" style={{ fontSize: 14 }}>Step 2 — Select Columns</div>
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontSize: 13 }}>GTIN Column <span className="text-danger">*</span></label>
                    <select className="form-select form-select-sm" value={gtinCol} onChange={e => setGtinCol(e.target.value)}>
                      <option value="">— select —</option>
                      {parsed.headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontSize: 13 }}>Description Column <span className="text-muted">(for auto-matching)</span></label>
                    <select className="form-select form-select-sm" value={descCol} onChange={e => setDescCol(e.target.value)}>
                      <option value="">— skip —</option>
                      {parsed.headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <button className="btn btn-primary btn-sm w-100" onClick={runMatch} disabled={!gtinCol}>
                      Match GTINs &rarr;
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3 */}
          {matches.length > 0 && (
            <div className="card mb-3">
              <div className="card-header d-flex align-items-center justify-content-between" style={{ fontSize: 14 }}>
                <span className="fw-semibold">Step 3 — Review &amp; Confirm</span>
                <span className="badge text-bg-secondary">{assignedCount} / {matches.length} assigned</span>
              </div>
              <div className="card-body p-0">
                <div style={{ overflowX: 'auto' }}>
                  <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 170 }}>GTIN</th>
                        <th>Description (from CSV)</th>
                        <th style={{ width: 340 }}>Assign To</th>
                        <th style={{ width: 80 }} className="text-center">Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matches.map((m, idx) => (
                        <tr key={idx} style={{ background: !m.selected ? '#fff8f8' : undefined }}>
                          <td className="font-monospace" style={{ letterSpacing: 1 }}>{m.gtin}</td>
                          <td className="text-muted" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.description || <em>—</em>}
                          </td>
                          <td>
                            <MatchCombobox
                              value={m.selected}
                              options={allOptions}
                              onChange={(type, id) => setSelected(idx, type, id)}
                            />
                          </td>
                          <td className="text-center">
                            {m.match ? confBadge(m.match.confidence) : <span className="text-muted small">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card-footer d-flex align-items-center gap-3">
                {error && <span className="text-danger small">{error}</span>}
                {saveResult && (
                  <span className="text-success small">✓ Saved {saveResult.saved} GTIN(s) successfully.</span>
                )}
                <button
                  className="btn btn-success ms-auto"
                  onClick={handleSave}
                  disabled={saving || assignedCount === 0}
                >
                  {saving ? 'Saving…' : `Save ${assignedCount} GTIN(s)`}
                </button>
              </div>
            </div>
          )}

          {parsed && matches.length === 0 && (
            <div className="text-muted text-center py-3" style={{ fontSize: 13 }}>
              No valid GTINs found after matching. Make sure you selected the correct GTIN column and clicked &ldquo;Match GTINs&rdquo;.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default GS1ImportPage;
